import { createFileRoute, redirect } from "@tanstack/react-router";

import { peekPostSsoRedirect } from "@/lib/ssoRedirect";

/**
 * `/login` — not a page, a landing pad for the Control Plane.
 *
 * CP's social sign-in failure paths redirect the browser to
 * `<OAUTH_FRONTEND_URL>/login?error=…` (`api/auth.py`, the Microsoft and Google
 * callbacks). The portal's sign-in page has always lived at
 * `/portal/sign-in`, so that target has never existed here — it simply never
 * showed, because `OAUTH_FRONTEND_URL` was unset in production and the whole
 * redirect went to `http://localhost:3000` instead.
 *
 * Setting that variable (lara-infrastructure#120, needed for the OAuth consent
 * page) makes these URLs real, so a failed Microsoft or Google sign-in would
 * land a customer on a page that does not exist. This forwards them to the
 * sign-in form with the reason intact, where `PortalSignInForm` already reads
 * `error` and shows it as a toast.
 *
 * `/settings/accounts` — CP's other frontend target, for account LINKING — is
 * deliberately not given one of these: nothing in the portal starts a link
 * flow, so that URL is unreachable. Add the same treatment if linking ships.
 */
export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  beforeLoad: ({ search }) => {
    // Carry the parked destination through the FAILURE leg too. The user was
    // on their way somewhere — an OAuth consent, typically — when the SSO
    // attempt failed; without this they sign in with a password, land on the
    // dashboard, and the authorisation expires with the request_id still
    // sitting unread in sessionStorage.
    //
    // PEEK, not take: `beforeLoad` runs more than once per navigation, and a
    // destructive read consumed the value on the first pass and redirected
    // with nothing on the second. Measured, not theorised — the spec for this
    // route failed exactly that way.
    const redirectTarget = peekPostSsoRedirect();
    throw redirect({
      to: "/portal/sign-in",
      search: {
        ...(search.error ? { error: search.error } : {}),
        ...(redirectTarget ? { redirect: redirectTarget } : {}),
      },
      replace: true,
    });
  },
});
